import { Box, Flex, Image, MediaBox, Typography } from "@jewlr/storybook/core"
import PropTypes from "prop-types"
import React from "react"
import styled from "styled-components"

import { IS_NA, IS_US, IS_CA, jewlr_t } from "helpers/application"
import { enableKlarnaCheckout } from "helpers/checkout/checkout_options"

import { Section, SectionTitle } from "../components"

const StyledSection = styled(Section).attrs({
  bg: { _: "white", tablet: "polar" },
  pb: { _: 4, tablet: 8 },
})``

const Grid = styled(Box).attrs((props) => ({
  display: "grid",
  gap: {
    _: "10px",
    desktop: props.count >= 6 ? "28px 46px" : "42px 80px",
    tablet: "42px 80px",
  },
  gridTemplateColumns: {
    _: "repeat(2, 1fr)",
    desktop: props.count >= 6 ? "repeat(3, 266px)" : "repeat(2, 266px)",
    tablet: "repeat(2, 266px)",
  },
  justifyContent: "center",
}))``

const PerkTitle = styled(Typography).attrs({
  as: "h3",
})`
  font-size: 18px;
  font-weight: 500;
  line-height: 22px;
  text-transform: capitalize;
  ${(props) => props.theme.mediaQueries.tablet`
    font-size: 20px;
    margin-bottom: 10px;
    margin-top: 8px;
  `}
`

const PerkDesc = styled(Typography)`
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  ${(props) => props.theme.mediaQueries.tablet`
    font-size: 16px;
    font-weight: 300;
    letter-spacing: -0.02em;
    line-height: 22px;
  `}
`

const PerkIcon = styled(Image).attrs({
  height: { _: "42px", tablet: "30px" },
  ratio: "1 / 1",
  width: { _: "42px", tablet: "30px" },
})``

const getPerks = (hasDiamondSelector, hasFreeResizing) => {
  const is_na = IS_NA()
  const is_us = IS_US()
  const is_ca = IS_CA()

  let perks = [
    {
      image: require("images/product/info/perks/returns.svg"),
      key: "99-day-returns",
      long_desc:
        "Buy with confidence! We offer an easy, hassle-free 99-day return policy.",
      short_desc: "No-hassle Policy",
      title: "99-Day Returns",
    },
  ]

  if (!hasDiamondSelector) {
    perks.push({
      image: require("images/product/info/perks/warranty.svg"),
      key: "free-warranty",
      long_desc: `We offer a one-year manufacturer's warranty on all ${jewlr_t(
        "terms.jewelry"
      )} and watches.`,
      short_desc: "Free One year Warranty",
      title: "1-Year Warranty",
    })
  }

  if (sl.config.enable_affirm || enableKlarnaCheckout()) {
    perks.push({
      image: require("images/product/info/perks/payment-plans.svg"),
      key: "payment-plans",
      long_desc:
        sl.config.enable_affirm && enableKlarnaCheckout()
          ? "Choose from Affirm or Klarna for flexible, affordable payment options."
          : "Choose Pay Later with Klarna to split your purchase into easy payments.",
      short_desc: "Flexible Options",
      title: "Payment Plans",
    })
  }

  if (sl.config.enable_extend_warranty) {
    perks.push({
      image: require("images/product/info/perks/protection-coverage.svg"),
      key: "protection-coverage",
      long_desc: `Enhance your warranty with optional extended coverage. Details in ${jewlr_t(
        "Cart"
      )}.`,
      mobile_title: "Extra Coverage",
      short_desc: "Protection Plans",
      title: "Add Protection Coverage",
    })
  }

  if (
    !hasDiamondSelector &&
    (!is_na || (is_us && hasFreeResizing) || (is_ca && !hasFreeResizing))
  ) {
    perks.push({
      image: require("images/product/info/perks/diamond.svg"),
      key: "handcrafted",
      long_desc:
        "Handcrafted by local artisans in our Canadian and US workshops.",
      mobile_title: "Handcrafted",
      short_desc: "In North America",
      title: "Made In North America",
    })
  }

  if (hasDiamondSelector && !is_us) {
    perks.push({
      image: require("images/product/info/perks/shipping.svg"),
      key: "shipping",
      long_desc: `Enjoy complimentary upgraded secure ${jewlr_t(
        "terms.shipping"
      )}.`,
      mobile_title: "Free Express",
      subtitle: jewlr_t("terms.Shipping"),
      title: `Free Express ${jewlr_t("terms.Shipping")}`,
    })
  }

  if (is_na && hasFreeResizing) {
    perks.push({
      image: require("images/product/info/perks/free-ring-sizing.svg"),
      key: "free-ring-sizing",
      long_desc:
        "We offer one free resizing within 30 days of the original shipment date. ",
      short_desc: "Within 30 Days",
      title: "Free Ring Sizing",
    })
  }

  if (!is_na && hasDiamondSelector) {
    perks.push({
      image: require("images/product/info/perks/lifetime-warranty.svg"),
      key: "warranty",
      long_desc: `All ${jewlr_t(
        "terms.jewelry"
      )} includes a warranty against manufacturer defects.`,
      short_desc: `On All ${jewlr_t("terms.Jewelry")}`,
      title: "Free Warranty",
    })
  }

  return perks
}

const Perks = ({ hasDiamondSelector, hasFreeResizing }) => {
  const perks = getPerks(hasDiamondSelector, hasFreeResizing)

  return (
    <StyledSection>
      <Box contain px={2}>
        <SectionTitle mb={{ _: 3, tablet: 6 }}>Why Choose Jewlr</SectionTitle>
        <Grid count={perks.length}>
          {perks.map((perk) => (
            <Flex
              alignItems={{ _: "center", tablet: "flex-start" }}
              flexDirection={{ _: "column", tablet: "row" }}
              gap={{ _: 2, tablet: "12px" }}
              key={perk.key}
              pb={{ _: 2, tablet: 0 }}
              pt={{ _: 1, tablet: 0 }}
            >
              <PerkIcon alt={perk.title} src={perk.image} />
              <MediaBox lessThan="tablet" textAlign="center">
                <PerkTitle>{perk.mobile_title || perk.title}</PerkTitle>
                {perk.subtitle && <PerkTitle>{perk.subtitle}</PerkTitle>}
                {perk.short_desc && (
                  <PerkDesc color="grey.65">{perk.short_desc}</PerkDesc>
                )}
              </MediaBox>
              <MediaBox greaterThanOrEqual="tablet">
                <PerkTitle>{perk.title}</PerkTitle>
                <PerkDesc>{perk.long_desc}</PerkDesc>
              </MediaBox>
            </Flex>
          ))}
        </Grid>
      </Box>
    </StyledSection>
  )
}

Perks.propTypes = {
  hasDiamondSelector: PropTypes.bool,
  hasFreeResizing: PropTypes.bool,
}

export default Perks
